/**
 * CoLab Canvas Background
 * Оптимізована версія з lazy rendering
 */

const canvas = document.getElementById('canvas');
if (!canvas) {
    console.log('[Canvas] Canvas element not found');
} else {
    initCanvasBackground();
}

// Все у функції, а не в else-блоці: function-декларації всередині блока
// esbuild перетворює на hoisted var (Annex B), і в зібраному чанку вони
// конфліктують з однойменними функціями інших модулів (init, resize...)
function initCanvasBackground() {
    const ctx = canvas.getContext('2d');

    let width, height;
    let drops = [];
    let animationId = null;
    let isVisible = true;
    let lastFrameTime = 0;
    const targetFPS = 30; // Знижуємо до 30 FPS (достатньо для фону)
    const frameInterval = 1000 / targetFPS;

    // Налаштування - ОПТИМІЗОВАНІ
    const config = {
        dropCount: 12,          // Менше крапель (було 18)
        minRadius: 40,
        maxRadius: 80,
        strokeWidth: 1.5,
        speed: 1.5,
        threshold: 1.0,
        resolution: 8           // Більший крок = менше обчислень (було 5)
    };

    const colors = ['#CCCCCC'];

    // ============================================
    // Visibility API - зупиняємо коли не видно
    // ============================================
    function handleVisibilityChange() {
        if (document.hidden) {
            isVisible = false;
            if (animationId) {
                cancelAnimationFrame(animationId);
                animationId = null;
            }
        } else {
            isVisible = true;
            if (!animationId) {
                lastFrameTime = performance.now();
                animate();
            }
        }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // ============================================
    // Intersection Observer - зупиняємо коли canvas не в viewport
    // ============================================
    const canvasObserver = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
            if (entry.isIntersecting) {
                isVisible = true;
                if (!animationId && !document.hidden) {
                    lastFrameTime = performance.now();
                    animate();
                }
            } else {
                isVisible = false;
                if (animationId) {
                    cancelAnimationFrame(animationId);
                    animationId = null;
                }
            }
        });
    }, { threshold: 0 });

    canvasObserver.observe(canvas);

    // ============================================
    // Resize з debounce
    // ============================================
    let resizeTimeout;
    function resize() {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(function() {
            width = canvas.width = window.innerWidth;
            height = canvas.height = window.innerHeight;
        }, 100);
    }

    function resizeImmediate() {
        width = canvas.width = window.innerWidth;
        height = canvas.height = window.innerHeight;
    }

    // ============================================
    // Drop Class - спрощена
    // ============================================
    class Drop {
        constructor(x, y, radius) {
            this.x = x ?? Math.random() * width;
            this.y = y ?? Math.random() * height;
            this.targetRadius = radius ?? config.minRadius + Math.random() * (config.maxRadius - config.minRadius);
            this.radius = this.targetRadius;
            this.radiusSq = this.radius * this.radius; // Кешуємо квадрат

            this.vx = (Math.random() - 0.5) * config.speed;
            this.vy = (Math.random() - 0.5) * config.speed;

            this.color = colors[0];
            this.canMerge = true;
            this.mergeTimer = 0;
            this.mergedAt = null;
            this.splitDelay = 250 + Math.random() * 150;
        }

        getArea() {
            return Math.PI * this.targetRadius * this.targetRadius;
        }

        update() {
            // Плавна зміна радіусу
            const radiusDiff = this.targetRadius - this.radius;
            if (Math.abs(radiusDiff) > 0.1) {
                this.radius += radiusDiff * 0.08;
                this.radiusSq = this.radius * this.radius;
            }

            // Рух
            this.x += this.vx;
            this.y += this.vy;

            // Таймер злиття
            if (!this.canMerge) {
                this.mergeTimer++;
                if (this.mergeTimer > 90) {
                    this.canMerge = true;
                }
            }

            // Тертя
            this.vx *= 0.995;
            this.vy *= 0.995;

            // Мінімальна швидкість
            const speedSq = this.vx * this.vx + this.vy * this.vy;
            if (speedSq < 0.0025) { // 0.05^2
                const angle = Math.random() * Math.PI * 2;
                this.vx += Math.cos(angle) * 0.03;
                this.vy += Math.sin(angle) * 0.03;
            }

            // Відбиття від країв
            const margin = this.radius + 10;
            if (this.x < margin) { this.x = margin; this.vx *= -0.7; }
            if (this.x > width - margin) { this.x = width - margin; this.vx *= -0.7; }
            if (this.y < margin) { this.y = margin; this.vy *= -0.7; }
            if (this.y > height - margin) { this.y = height - margin; this.vy *= -0.7; }
        }

        // Оптимізований field - без sqrt
        fieldAt(px, py) {
            const dx = px - this.x;
            const dy = py - this.y;
            const distSq = dx * dx + dy * dy;
            if (distSq < 1) return 1000;
            return this.radiusSq / distSq;
        }
    }

    // ============================================
    // Init
    // ============================================
    function init() {
        resizeImmediate();
        drops = [];

        for (let i = 0; i < config.dropCount; i++) {
            const drop = new Drop();
            let attempts = 0;
            while (attempts < 30) {
                let overlaps = false;
                for (const other of drops) {
                    const dx = drop.x - other.x;
                    const dy = drop.y - other.y;
                    const minDist = drop.radius + other.radius + 20;
                    if (dx * dx + dy * dy < minDist * minDist) {
                        overlaps = true;
                        break;
                    }
                }
                if (!overlaps) break;
                drop.x = Math.random() * width;
                drop.y = Math.random() * height;
                attempts++;
            }
            drops.push(drop);
        }
    }

    // ============================================
    // Optimized Marching Squares
    // ============================================
    function drawMetaballGroup(groupDrops, color) {
        if (groupDrops.length === 0) return;

        const res = config.resolution;
        const threshold = config.threshold;

        // Bounding box
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (let i = 0; i < groupDrops.length; i++) {
            const drop = groupDrops[i];
            const r2 = drop.radius * 2;
            if (drop.x - r2 < minX) minX = drop.x - r2;
            if (drop.y - r2 < minY) minY = drop.y - r2;
            if (drop.x + r2 > maxX) maxX = drop.x + r2;
            if (drop.y + r2 > maxY) maxY = drop.y + r2;
        }

        minX = Math.max(0, minX - 15);
        minY = Math.max(0, minY - 15);
        maxX = Math.min(width, maxX + 15);
        maxY = Math.min(height, maxY + 15);

        // Локальна функція поля
        function groupField(px, py) {
            let sum = 0;
            for (let i = 0; i < groupDrops.length; i++) {
                sum += groupDrops[i].fieldAt(px, py);
            }
            return sum;
        }

        ctx.beginPath();

        // Кешуємо значення попереднього рядка
        const cols = Math.ceil((maxX - minX) / res) + 1;
        let prevRow = new Float32Array(cols);
        let currRow = new Float32Array(cols);

        // Заповнюємо перший рядок
        for (let i = 0; i < cols; i++) {
            prevRow[i] = groupField(minX + i * res, minY);
        }

        for (let y = minY; y < maxY; y += res) {
            // Заповнюємо поточний рядок
            for (let i = 0; i < cols; i++) {
                currRow[i] = groupField(minX + i * res, y + res);
            }

            for (let i = 0; i < cols - 1; i++) {
                const x = minX + i * res;

                const f_tl = prevRow[i];
                const f_tr = prevRow[i + 1];
                const f_bl = currRow[i];
                const f_br = currRow[i + 1];

                const tl = f_tl >= threshold ? 8 : 0;
                const tr = f_tr >= threshold ? 4 : 0;
                const br = f_br >= threshold ? 2 : 0;
                const bl = f_bl >= threshold ? 1 : 0;

                const state = tl + tr + br + bl;

                if (state === 0 || state === 15) continue;

                // Інтерполяція
                const getT = (v1, v2) => {
                    const diff = v2 - v1;
                    if (Math.abs(diff) < 0.0001) return 0.5;
                    return (threshold - v1) / diff;
                };

                const top_x = x + res * getT(f_tl, f_tr);
                const right_y = y + res * getT(f_tr, f_br);
                const bottom_x = x + res * getT(f_bl, f_br);
                const left_y = y + res * getT(f_tl, f_bl);

                switch (state) {
                    case 1: ctx.moveTo(x, left_y); ctx.lineTo(bottom_x, y + res); break;
                    case 2: ctx.moveTo(bottom_x, y + res); ctx.lineTo(x + res, right_y); break;
                    case 3: ctx.moveTo(x, left_y); ctx.lineTo(x + res, right_y); break;
                    case 4: ctx.moveTo(top_x, y); ctx.lineTo(x + res, right_y); break;
                    case 5:
                        ctx.moveTo(x, left_y); ctx.lineTo(top_x, y);
                        ctx.moveTo(bottom_x, y + res); ctx.lineTo(x + res, right_y);
                        break;
                    case 6: ctx.moveTo(top_x, y); ctx.lineTo(bottom_x, y + res); break;
                    case 7: ctx.moveTo(x, left_y); ctx.lineTo(top_x, y); break;
                    case 8: ctx.moveTo(top_x, y); ctx.lineTo(x, left_y); break;
                    case 9: ctx.moveTo(top_x, y); ctx.lineTo(bottom_x, y + res); break;
                    case 10:
                        ctx.moveTo(top_x, y); ctx.lineTo(x + res, right_y);
                        ctx.moveTo(x, left_y); ctx.lineTo(bottom_x, y + res);
                        break;
                    case 11: ctx.moveTo(top_x, y); ctx.lineTo(x + res, right_y); break;
                    case 12: ctx.moveTo(x, left_y); ctx.lineTo(x + res, right_y); break;
                    case 13: ctx.moveTo(bottom_x, y + res); ctx.lineTo(x + res, right_y); break;
                    case 14: ctx.moveTo(x, left_y); ctx.lineTo(bottom_x, y + res); break;
                }
            }

            // Swap rows
            const temp = prevRow;
            prevRow = currRow;
            currRow = temp;
        }

        ctx.strokeStyle = color;
        ctx.lineWidth = config.strokeWidth;
        ctx.lineCap = 'round';
        ctx.stroke();
    }

    // ============================================
    // Physics - спрощено
    // ============================================
    function applyForces() {
        const len = drops.length;
        for (let i = 0; i < len; i++) {
            const drop1 = drops[i];
            for (let j = i + 1; j < len; j++) {
                const drop2 = drops[j];

                const dx = drop2.x - drop1.x;
                const dy = drop2.y - drop1.y;
                const distSq = dx * dx + dy * dy;
                const minDist = drop1.radius + drop2.radius;
                const minDistSq = minDist * minDist;

                if (distSq < minDistSq * 2.25 && distSq > 0) {
                    const distance = Math.sqrt(distSq);
                    const nx = dx / distance;
                    const ny = dy / distance;

                    if (drop1.canMerge && drop2.canMerge && distSq < minDistSq * 0.64) {
                        const attraction = 0.008;
                        drop1.vx += nx * attraction;
                        drop1.vy += ny * attraction;
                        drop2.vx -= nx * attraction;
                        drop2.vy -= ny * attraction;
                    }
                }

                // Легке притягування на відстані
                if (distSq < minDistSq * 9 && distSq > minDistSq) {
                    const distance = Math.sqrt(distSq);
                    const attraction = 0.0003;
                    drop1.vx += (dx / distance) * attraction;
                    drop1.vy += (dy / distance) * attraction;
                    drop2.vx -= (dx / distance) * attraction;
                    drop2.vy -= (dy / distance) * attraction;
                }
            }
        }
    }

    function checkMerging() {
        const toRemove = [];
        const toAdd = [];

        for (let i = 0; i < drops.length; i++) {
            if (toRemove.includes(i)) continue;

            for (let j = i + 1; j < drops.length; j++) {
                if (toRemove.includes(j)) continue;

                const drop1 = drops[i];
                const drop2 = drops[j];

                if (!drop1.canMerge || !drop2.canMerge) continue;

                const dx = drop2.x - drop1.x;
                const dy = drop2.y - drop1.y;
                const distSq = dx * dx + dy * dy;
                const mergeThreshold = (drop1.radius + drop2.radius) * 0.5;

                if (distSq < mergeThreshold * mergeThreshold) {
                    const newArea = drop1.getArea() + drop2.getArea();
                    const newRadius = Math.sqrt(newArea / Math.PI);

                    if (newRadius > config.maxRadius * 2.2) continue;

                    const totalMass = newArea;
                    const a1 = drop1.getArea();
                    const a2 = drop2.getArea();

                    const newDrop = new Drop(
                        (drop1.x * a1 + drop2.x * a2) / totalMass,
                        (drop1.y * a1 + drop2.y * a2) / totalMass,
                        Math.max(drop1.radius, drop2.radius)
                    );
                    newDrop.targetRadius = newRadius;
                    newDrop.vx = (drop1.vx * a1 + drop2.vx * a2) / totalMass;
                    newDrop.vy = (drop1.vy * a1 + drop2.vy * a2) / totalMass;
                    newDrop.canMerge = false;
                    newDrop.mergedAt = Date.now();
                    newDrop.splitDelay = 300 + Math.random() * 200;

                    toRemove.push(i, j);
                    toAdd.push(newDrop);
                    break;
                }
            }
        }

        // Видаляємо в зворотному порядку
        toRemove.sort((a, b) => b - a);
        for (const idx of toRemove) {
            drops.splice(idx, 1);
        }
        drops.push(...toAdd);
    }

    function checkSplitting() {
        const toRemove = [];
        const toAdd = [];
        const now = Date.now();

        for (let i = 0; i < drops.length; i++) {
            const drop = drops[i];

            if (drop.mergedAt && now - drop.mergedAt > drop.splitDelay * 16) {
                if (drop.radius > config.maxRadius * 0.8) {
                    toRemove.push(i);

                    const area = drop.getArea();
                    const ratio = 0.4 + Math.random() * 0.2;
                    const radius1 = Math.sqrt(area * ratio / Math.PI);
                    const radius2 = Math.sqrt(area * (1 - ratio) / Math.PI);

                    const angle = Math.random() * Math.PI * 2;
                    const cos = Math.cos(angle);
                    const sin = Math.sin(angle);
                    const splitSpeed = 1 + Math.random() * 0.5;

                    const drop1 = new Drop(drop.x + cos * 5, drop.y + sin * 5, drop.radius * 0.7);
                    drop1.targetRadius = radius1;
                    drop1.vx = drop.vx + cos * splitSpeed;
                    drop1.vy = drop.vy + sin * splitSpeed;
                    drop1.canMerge = false;

                    const drop2 = new Drop(drop.x - cos * 5, drop.y - sin * 5, drop.radius * 0.7);
                    drop2.targetRadius = radius2;
                    drop2.vx = drop.vx - cos * splitSpeed;
                    drop2.vy = drop.vy - sin * splitSpeed;
                    drop2.canMerge = false;

                    toAdd.push(drop1, drop2);
                }
            }
        }

        for (let i = toRemove.length - 1; i >= 0; i--) {
            drops.splice(toRemove[i], 1);
        }
        drops.push(...toAdd);
    }

    // ============================================
    // Animation Loop з FPS throttling
    // ============================================
    function animate(currentTime) {
        if (!isVisible) return;

        animationId = requestAnimationFrame(animate);

        // FPS throttling
        const elapsed = currentTime - lastFrameTime;
        if (elapsed < frameInterval) return;
        lastFrameTime = currentTime - (elapsed % frameInterval);

        // Clear
        ctx.fillStyle = '#F5F5F5';
        ctx.fillRect(0, 0, width, height);

        // Update
        for (let i = 0; i < drops.length; i++) {
            drops[i].update();
        }

        applyForces();
        checkMerging();
        checkSplitting();

        // Draw
        drawMetaballGroup(drops, colors[0]);
    }

    // ============================================
    // Events
    // ============================================
    window.addEventListener('resize', resize);

    // Click handling - спрощено
    canvas.addEventListener('click', function(e) {
        const clickX = e.clientX;
        const clickY = e.clientY;

        let closestIdx = -1;
        let closestDistSq = Infinity;

        for (let i = 0; i < drops.length; i++) {
            const dx = drops[i].x - clickX;
            const dy = drops[i].y - clickY;
            const distSq = dx * dx + dy * dy;
            const maxDist = drops[i].radius + 50;

            if (distSq < maxDist * maxDist && distSq < closestDistSq) {
                closestDistSq = distSq;
                closestIdx = i;
            }
        }

        if (closestIdx !== -1) {
            const drop = drops[closestIdx];
            const numPieces = 3;
            const pieceArea = drop.getArea() / numPieces;
            const pieceRadius = Math.max(config.minRadius * 0.7, Math.sqrt(pieceArea / Math.PI));

            const newDrops = [];
            for (let i = 0; i < numPieces; i++) {
                const angle = (Math.PI * 2 / numPieces) * i + Math.random() * 0.3;
                const speed = 1.5 + Math.random();

                const newDrop = new Drop(
                    drop.x + Math.cos(angle) * drop.radius * 0.3,
                    drop.y + Math.sin(angle) * drop.radius * 0.3,
                    drop.radius * 0.5
                );
                newDrop.targetRadius = pieceRadius;
                newDrop.vx = Math.cos(angle) * speed;
                newDrop.vy = Math.sin(angle) * speed;
                newDrop.canMerge = false;
                newDrops.push(newDrop);
            }

            drops.splice(closestIdx, 1);
            drops.push(...newDrops);
        }
    });

    // ============================================
    // Start
    // ============================================
    init();
    lastFrameTime = performance.now();
    animate(lastFrameTime);
}

export default {};
